import { Instance, SnapshotIn, action, register, types } from "@gadgetinc/mobx-quick-tree";
import { GadgetDomainModelClass, contentHash, getAppSettings } from "../utils";
import type { AppSettings } from "../AppSettings";
import { encryptApplicationSecret } from "../appSecrets";
import { EnvironmentConfiguration } from "../EnvironmentConfig";

export const BIG_COMMERCE_DEFAULT_INSTALL_PATH = "api/bigcommerce/install";
export const BIG_COMMERCE_DEFAULT_UNINSTALL_PATH = "api/bigcommerce/uninstall";
export const BIG_COMMERCE_DEFAULT_LOAD_PATH = "api/bigcommerce/load";
export const BIG_COMMERCE_DEFAULT_REMOVE_USER_PATH = "api/bigcommerce/remove-user";

@register
export class BigCommerceSingleClickAppConfig extends GadgetDomainModelClass("BigCommerceSingleClickAppConfig", {
  authCallbackUrl: types.string,
  loadCallbackUrl: types.string,
  uninstallCallbackUrl: types.string,
  removeUserCallbackUrl: types.string,
  appName: types.maybe(types.string),
  clientId: types.string,
  clientSecret: types.string,
  hasHadStoreInstall: types.optional(types.boolean, false),
}) {
  get contentHash() {
    return contentHash([
      this.authCallbackUrl,
      this.loadCallbackUrl,
      this.uninstallCallbackUrl,
      this.removeUserCallbackUrl,
      this.clientId,
      this.clientSecret,
    ]);
  }

  @action
  setAppName(value?: string) {
    this.appName = value;
  }
  @action
  setClientId(value: string) {
    this.clientId = value;
  }
  @action
  setClientSecret(value: string) {
    const publicKey = getAppSettings(this).secretsPublicKey;
    this.clientSecret = encryptApplicationSecret(value, publicKey);
  }
  @action
  clearClientSecret() {
    this.clientSecret = "";
  }
  @action
  setHasHadStoreInstall(value: boolean) {
    this.hasHadStoreInstall = value;
  }
}

export const defaultAuthCallbackUrl = (settings: AppSettings) => {
  return `${settings.defaultDomain}${BIG_COMMERCE_DEFAULT_INSTALL_PATH}`;
};

export const defaultLoadCallbackUrl = (settings: AppSettings) => {
  return `${settings.defaultDomain}${BIG_COMMERCE_DEFAULT_LOAD_PATH}`;
};

export const defaultUninstallCallbackUrl = (settings: AppSettings) => {
  return `${settings.defaultDomain}${BIG_COMMERCE_DEFAULT_UNINSTALL_PATH}`;
};

export const defaultRemoveUserCallbackUrl = (settings: AppSettings) => {
  return `${settings.defaultDomain}${BIG_COMMERCE_DEFAULT_REMOVE_USER_PATH}`;
};

export const defaultBigCommerceSingleClickAppConfig = (opts: {
  settings: AppSettings;
  clientId: string;
  clientSecret: string;
}): SnapshotIn<typeof BigCommerceSingleClickAppConfig> => {
  const clientSecret = encryptApplicationSecret(opts.clientSecret, opts.settings.secretsPublicKey);

  return {
    type: "BigCommerceSingleClickAppConfig",
    authCallbackUrl: defaultAuthCallbackUrl(opts.settings),
    loadCallbackUrl: defaultLoadCallbackUrl(opts.settings),
    uninstallCallbackUrl: defaultUninstallCallbackUrl(opts.settings),
    removeUserCallbackUrl: defaultRemoveUserCallbackUrl(opts.settings),
    clientId: opts.clientId,
    clientSecret,
  };
};

export const isBigCommerceSingleClickAppConfig = (
  appConfig: Instance<typeof EnvironmentConfiguration>
): appConfig is Instance<typeof BigCommerceSingleClickAppConfig> => {
  return BigCommerceSingleClickAppConfig.is(appConfig);
};
